import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getConversionFactor } from '../data/foodDatabase';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const { FiCheckCircle, FiAlertCircle, FiTrendingUp, FiPieChart, FiTarget, FiEdit2, FiSave, 
       FiAlertTriangle, FiInfo, FiPlus, FiMinus, FiChevronRight, FiTrash2, FiShare2, FiMail, FiInstagram, FiBriefcase, FiDownload, FiFileText } = FiIcons;

const MealResults = ({ mealPlan, targets, thresholds, onMealPlanUpdate, selectedFoods, onAddFood, onRemoveFood }) => {
  // Initialize state variables unconditionally at the top level
  const [editMode, setEditMode] = useState(false);
  const [editedFoods, setEditedFoods] = useState([]);
  const [editedTotals, setEditedTotals] = useState({});
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [showSmartRecommendation, setShowSmartRecommendation] = useState(false);
  const [currentRecommendation, setCurrentRecommendation] = useState(null);
  const [recommendationApplied, setRecommendationApplied] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  
  // useEffect must be called unconditionally
  useEffect(() => {
    // Only update state if mealPlan exists
    if (mealPlan) {
      setEditedFoods(mealPlan.foods);
      setEditedTotals(mealPlan.totals);
      setEditMode(false);
      
      // Check if we should show smart recommendations automatically
      const shouldShowRecommendation = mealPlan.foods && mealPlan.foods.length > 0 && 
        !isWithinAllThresholds(mealPlan.totals, targets, thresholds);
      
      if (shouldShowRecommendation) {
        generateHolisticRecommendation(mealPlan.foods, targets, thresholds, selectedFoods);
      } else {
        setCurrentRecommendation(null);
        setShowSmartRecommendation(false);
      }
    }
  }, [mealPlan]);

  // Early return pattern after all hooks have been called
  if (!mealPlan) {
    return null;
  }

  const { totals } = mealPlan;
  const targetCals = parseFloat(targets.calories);
  const targetProtein = parseFloat(targets.protein) || 0;
  const targetCarbs = parseFloat(targets.carbs) || 0;
  const targetFat = parseFloat(targets.fat) || 0;

  // Check if this is a failed optimization
  const isOptimizationFailed = !mealPlan.foods || mealPlan.foods.length === 0;

  const getAccuracyStatus = (actual, target, threshold) => {
    if (!target || !threshold) return { status: 'neutral', percentage: 0 };
    const percentage = ((actual - target) / target) * 100;
    const isWithinThreshold = Math.abs(percentage) <= threshold;
    return {
      status: isWithinThreshold ? 'success' : 'warning',
      percentage: Math.round(percentage * 10) / 10
    };
  };

  // Check if all macros are within thresholds
  const isWithinAllThresholds = (totals, targets, thresholds) => {
    const calorieStatus = getAccuracyStatus(totals.calories, targetCals, thresholds.calories);
    
    if (calorieStatus.status !== 'success') return false;
    
    if (thresholds.advancedMode) {
      const proteinStatus = getAccuracyStatus(totals.protein, targetProtein, thresholds.protein);
      const carbsStatus = getAccuracyStatus(totals.carbs, targetCarbs, thresholds.carbs);
      const fatStatus = getAccuracyStatus(totals.fat, targetFat, thresholds.fat);
      
      return (proteinStatus.status === 'success' || proteinStatus.status === 'neutral') &&
             (carbsStatus.status === 'success' || carbsStatus.status === 'neutral') &&
             (fatStatus.status === 'success' || fatStatus.status === 'neutral');
    }
    
    return true;
  };

  // Simulate the effect of adjusting a food's serving amount
  const simulateAdjustment = (foods, foodId, newAmount) => {
    const updatedFoods = foods.map(food => {
      if (food.id === foodId) {
        // Get conversion factor for selected serving unit
        const conversionFactor = getConversionFactor(food, food.selectedServingUnit);
        
        // Convert to base serving size for nutrition calculation
        const baseServingMultiplier = (newAmount * conversionFactor) / food.servingSize;

        return {
          ...food,
          actualServingAmount: newAmount,
          actualCalories: food.calories * baseServingMultiplier,
          actualProtein: food.protein * baseServingMultiplier,
          actualCarbs: food.carbs * baseServingMultiplier,
          actualFat: food.fat * baseServingMultiplier
        };
      }
      return food;
    });

    // Calculate new totals
    const newTotals = updatedFoods.reduce((acc, item) => ({
      calories: acc.calories + item.actualCalories,
      protein: acc.protein + item.actualProtein,
      carbs: acc.carbs + item.actualCarbs,
      fat: acc.fat + item.actualFat
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

    return { updatedFoods, newTotals };
  };

  // Simulate the effect of adding a new food
  const simulateAddFood = (foods, foodToAdd, amount) => {
    // Get conversion factor for selected serving unit
    const conversionFactor = getConversionFactor(foodToAdd, foodToAdd.selectedServingUnit);
    
    // Convert to base serving size for nutrition calculation
    const baseServingMultiplier = (amount * conversionFactor) / foodToAdd.servingSize;

    const newFood = {
      ...foodToAdd,
      actualServingAmount: amount,
      actualCalories: foodToAdd.calories * baseServingMultiplier,
      actualProtein: foodToAdd.protein * baseServingMultiplier,
      actualCarbs: foodToAdd.carbs * baseServingMultiplier,
      actualFat: foodToAdd.fat * baseServingMultiplier
    };

    const updatedFoods = [...foods, newFood];

    // Calculate new totals
    const newTotals = updatedFoods.reduce((acc, item) => ({
      calories: acc.calories + item.actualCalories,
      protein: acc.protein + item.actualProtein,
      carbs: acc.carbs + item.actualCarbs,
      fat: acc.fat + item.actualFat
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

    return { updatedFoods, newTotals };
  };

  // Simulate the effect of removing a food
  const simulateRemoveFood = (foods, foodId) => {
    const updatedFoods = foods.filter(food => food.id !== foodId);
    
    // Calculate new totals
    const newTotals = updatedFoods.reduce((acc, item) => ({
      calories: acc.calories + item.actualCalories,
      protein: acc.protein + item.actualProtein,
      carbs: acc.carbs + item.actualCarbs,
      fat: acc.fat + item.actualFat
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

    return { updatedFoods, newTotals };
  };

  // Calculate how well a set of totals matches all targets
  const calculateMacroFitScore = (totals, targets, thresholds) => {
    let score = 0;
    
    // Calculate calorie score - most important
    const calorieDiff = Math.abs(totals.calories - targetCals) / targetCals;
    score += calorieDiff * 3; // Higher weight for calories
    
    if (thresholds.advancedMode) {
      // Calculate protein score
      if (targetProtein > 0) {
        const proteinDiff = Math.abs(totals.protein - targetProtein) / targetProtein;
        score += proteinDiff;
      }
      
      // Calculate carbs score
      if (targetCarbs > 0) {
        const carbsDiff = Math.abs(totals.carbs - targetCarbs) / targetCarbs;
        score += carbsDiff;
      }
      
      // Calculate fat score
      if (targetFat > 0) {
        const fatDiff = Math.abs(totals.fat - targetFat) / targetFat;
        score += fatDiff;
      }
    }
    
    return score;
  };

  // Check if all macros are within thresholds
  const checkAllMacrosWithinThresholds = (totals, targets, thresholds) => {
    // Check calories
    const calorieDiff = Math.abs(totals.calories - targetCals) / targetCals;
    if (calorieDiff > (thresholds.calories / 100)) return false;
    
    if (thresholds.advancedMode) {
      // Check protein
      if (targetProtein > 0) {
        const proteinDiff = Math.abs(totals.protein - targetProtein) / targetProtein;
        if (proteinDiff > (thresholds.protein / 100)) return false;
      }
      
      // Check carbs
      if (targetCarbs > 0) {
        const carbsDiff = Math.abs(totals.carbs - targetCarbs) / targetCarbs;
        if (carbsDiff > (thresholds.carbs / 100)) return false;
      }
      
      // Check fat
      if (targetFat > 0) {
        const fatDiff = Math.abs(totals.fat - targetFat) / targetFat;
        if (fatDiff > (thresholds.fat / 100)) return false;
      }
    }
    
    return true;
  };

  // Generate a comprehensive recommendation that balances all macros
  const generateHolisticRecommendation = (currentFoods, targets, thresholds, availableFoods) => {
    // If no foods, nothing to recommend
    if (!currentFoods || currentFoods.length === 0) {
      setCurrentRecommendation(null);
      setShowSmartRecommendation(false);
      return;
    }
    
    // Current macro status
    const currentTotals = currentFoods.reduce((acc, item) => ({
      calories: acc.calories + item.actualCalories,
      protein: acc.protein + item.actualProtein,
      carbs: acc.carbs + item.actualCarbs,
      fat: acc.fat + item.actualFat
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
    
    const calorieStatus = getAccuracyStatus(currentTotals.calories, targetCals, thresholds.calories);
    const proteinStatus = thresholds.advancedMode ? getAccuracyStatus(currentTotals.protein, targetProtein, thresholds.protein) : { status: 'neutral' };
    const carbsStatus = thresholds.advancedMode ? getAccuracyStatus(currentTotals.carbs, targetCarbs, thresholds.carbs) : { status: 'neutral' };
    const fatStatus = thresholds.advancedMode ? getAccuracyStatus(currentTotals.fat, targetFat, thresholds.fat) : { status: 'neutral' };

    // If all macros are within thresholds, no recommendation needed
    if (calorieStatus.status === 'success' && 
        (proteinStatus.status === 'success' || proteinStatus.status === 'neutral') && 
        (carbsStatus.status === 'success' || carbsStatus.status === 'neutral') && 
        (fatStatus.status === 'success' || fatStatus.status === 'neutral')) {
      setCurrentRecommendation(null);
      setShowSmartRecommendation(false);
      return;
    }

    // Try to find the best holistic adjustment
    let bestAdjustment = null;
    let bestScore = Infinity;
    let bestNewTotals = null;
    let bestAction = null;

    // 1. Try adjusting existing foods first
    for (const food of currentFoods) {
      const minServingAmount = food.desiredMin;
      const maxServingAmount = food.desiredMax;
      const currentAmount = food.actualServingAmount;

      // Try increasing
      if (currentAmount < maxServingAmount) {
        // Try different amounts within allowed range
        for (let amount = currentAmount + 0.5; amount <= maxServingAmount; amount += 0.5) {
          // Round to appropriate precision
          const roundedAmount = roundToHalf(amount, food.selectedServingUnit);
          
          // Simulate the adjustment
          const { updatedFoods, newTotals } = simulateAdjustment(currentFoods, food.id, roundedAmount);
          
          // Calculate how well this meets all targets
          const score = calculateMacroFitScore(newTotals, targets, thresholds);
          
          // If this is better than our current best and improves the original score
          const originalScore = calculateMacroFitScore(currentTotals, targets, thresholds);
          if (score < bestScore && score < originalScore) {
            bestScore = score;
            bestAdjustment = {
              type: 'adjust',
              food,
              newAmount: roundedAmount,
              action: 'increase'
            };
            bestNewTotals = newTotals;
            bestAction = 'increase';
          }
        }
      }

      // Try decreasing
      if (currentAmount > minServingAmount) {
        // Try different amounts within allowed range
        for (let amount = currentAmount - 0.5; amount >= minServingAmount; amount -= 0.5) {
          // Round to appropriate precision
          const roundedAmount = roundToHalf(amount, food.selectedServingUnit);
          
          // Simulate the adjustment
          const { updatedFoods, newTotals } = simulateAdjustment(currentFoods, food.id, roundedAmount);
          
          // Calculate how well this meets all targets
          const score = calculateMacroFitScore(newTotals, targets, thresholds);
          
          // If this is better than our current best and improves the original score
          const originalScore = calculateMacroFitScore(currentTotals, targets, thresholds);
          if (score < bestScore && score < originalScore) {
            bestScore = score;
            bestAdjustment = {
              type: 'adjust',
              food,
              newAmount: roundedAmount,
              action: 'decrease'
            };
            bestNewTotals = newTotals;
            bestAction = 'decrease';
          }
        }
      }
    }

    // 2. Try removing foods if that would improve the macros significantly
    // Only if we have more than one food in the meal
    if (currentFoods.length > 1) {
      for (const food of currentFoods) {
        // Simulate removing this food
        const { updatedFoods, newTotals } = simulateRemoveFood(currentFoods, food.id);
        
        // Calculate how well this meets all targets
        const score = calculateMacroFitScore(newTotals, targets, thresholds);
        
        // If this is better than our current best and improves the original score
        const originalScore = calculateMacroFitScore(currentTotals, targets, thresholds);
        if (score < bestScore && score < originalScore) {
          bestScore = score;
          bestAdjustment = {
            type: 'remove',
            food,
            action: 'remove'
          };
          bestNewTotals = newTotals;
          bestAction = 'remove';
        }
      }
    }

    // 3. If no good adjustment found or still not within thresholds, try adding a new food
    if (!bestAdjustment || (bestNewTotals && !checkAllMacrosWithinThresholds(bestNewTotals, targets, thresholds))) {
      // Filter out foods that are already in the meal
      const unusedFoods = availableFoods.filter(food => 
        !currentFoods.some(current => current.id === food.id)
      );
      
      if (unusedFoods.length > 0) {
        for (const foodToAdd of unusedFoods) {
          // Set default serving amount based on food type
          const minAmount = foodToAdd.servingUnit === 'g' ? 50 : 1;
          const maxAmount = foodToAdd.servingUnit === 'g' ? 200 : 3;
          
          // Try different amounts
          for (let amount = minAmount; amount <= maxAmount; amount += foodToAdd.servingUnit === 'g' ? 25 : 0.5) {
            // Simulate adding this food
            const { updatedFoods, newTotals } = simulateAddFood(currentFoods, {
              ...foodToAdd,
              desiredMin: minAmount,
              desiredMax: maxAmount,
              selectedServingUnit: foodToAdd.servingUnit
            }, amount);
            
            // Calculate how well this meets all targets
            const score = calculateMacroFitScore(newTotals, targets, thresholds);
            
            // If this is better than our current best
            if (score < bestScore) {
              bestScore = score;
              bestAdjustment = {
                type: 'add',
                food: {
                  ...foodToAdd,
                  desiredMin: minAmount,
                  desiredMax: maxAmount,
                  selectedServingUnit: foodToAdd.servingUnit
                },
                amount
              };
              bestNewTotals = newTotals;
              bestAction = 'add';
            }
          }
        }
      }
    }

    // 4. If no good single adjustment found, try combining multiple adjustments
    if (!bestAdjustment || (bestNewTotals && !checkAllMacrosWithinThresholds(bestNewTotals, targets, thresholds))) {
      // This would be complex to implement fully, as it requires trying many combinations
      // For now, we'll just use the best single adjustment we found
    }

    // Create the recommendation based on the best adjustment
    let recommendation = null;
    
    if (bestAdjustment) {
      if (bestAdjustment.type === 'adjust') {
        const food = bestAdjustment.food;
        const newAmount = bestAdjustment.newAmount;
        const action = bestAdjustment.action;
        
        // Format the change in macros
        const { newTotals } = simulateAdjustment(currentFoods, food.id, newAmount);
        const calorieDiff = Math.round(newTotals.calories - currentTotals.calories);
        const proteinDiff = Math.round((newTotals.protein - currentTotals.protein) * 10) / 10;
        const carbsDiff = Math.round((newTotals.carbs - currentTotals.carbs) * 10) / 10;
        const fatDiff = Math.round((newTotals.fat - currentTotals.fat) * 10) / 10;
        
        // Determine the primary macro affected for the title
        let primaryMacro = 'meal balance';
        let macroAdjustment = 'overall';
        
        // Check which macro changed the most proportionally
        const proteinPctChange = targetProtein > 0 ? Math.abs(proteinDiff / targetProtein) : 0;
        const carbsPctChange = targetCarbs > 0 ? Math.abs(carbsDiff / targetCarbs) : 0;
        const fatPctChange = targetFat > 0 ? Math.abs(fatDiff / targetFat) : 0;
        const calPctChange = Math.abs(calorieDiff / targetCals);
        
        if (calPctChange > proteinPctChange && calPctChange > carbsPctChange && calPctChange > fatPctChange) {
          primaryMacro = 'calorie balance';
          macroAdjustment = 'calories';
        } else if (proteinPctChange > carbsPctChange && proteinPctChange > fatPctChange) {
          primaryMacro = 'protein balance';
          macroAdjustment = 'protein';
        } else if (carbsPctChange > fatPctChange) {
          primaryMacro = 'carbohydrate balance';
          macroAdjustment = 'carbs';
        } else {
          primaryMacro = 'fat balance';
          macroAdjustment = 'fat';
        }
        
        recommendation = {
          type: 'adjust',
          title: `${action === 'increase' ? 'Increase' : 'Decrease'} ${food.name} for Better ${primaryMacro.charAt(0).toUpperCase() + primaryMacro.slice(1)}`,
          description: `${action === 'increase' ? 'Increase' : 'Decrease'} ${food.name} from ${food.actualServingAmount} to ${Math.round(newAmount * 10) / 10} ${food.selectedServingUnit} to ${action === 'increase' ? 'add' : 'remove'} approximately ${Math.abs(calorieDiff)} calories, ${Math.abs(proteinDiff)}g protein, ${Math.abs(carbsDiff)}g carbs, and ${Math.abs(fatDiff)}g fat.`,
          action,
          actionLabel: `${action === 'increase' ? 'Increase' : 'Decrease'} Portion`,
          macroAdjustment,
          foodToAdjust: food,
          newAmount: Math.round(newAmount * 10) / 10,
          macroChanges: {
            calories: calorieDiff,
            protein: proteinDiff,
            carbs: carbsDiff,
            fat: fatDiff
          }
        };
      } else if (bestAdjustment.type === 'add') {
        const foodToAdd = bestAdjustment.food;
        const amount = bestAdjustment.amount;
        
        // Convert to base serving size for nutrition calculation
        const conversionFactor = getConversionFactor(foodToAdd, foodToAdd.selectedServingUnit);
        const baseServingMultiplier = (amount * conversionFactor) / foodToAdd.servingSize;
        
        // Calculate the macro changes
        const caloriesAdded = Math.round(foodToAdd.calories * baseServingMultiplier);
        const proteinAdded = Math.round((foodToAdd.protein * baseServingMultiplier) * 10) / 10;
        const carbsAdded = Math.round((foodToAdd.carbs * baseServingMultiplier) * 10) / 10;
        const fatAdded = Math.round((foodToAdd.fat * baseServingMultiplier) * 10) / 10;
        
        // Determine which macro this food primarily adds
        let primaryMacro = 'balanced nutrition';
        let macroAdjustment = 'overall';
        
        // Check which macro is most significantly affected
        const proteinPct = foodToAdd.protein * 4 / foodToAdd.calories;
        const carbsPct = foodToAdd.carbs * 4 / foodToAdd.calories;
        const fatPct = foodToAdd.fat * 9 / foodToAdd.calories;
        
        if (proteinPct > 0.5) {
          primaryMacro = 'protein';
          macroAdjustment = 'protein';
        } else if (carbsPct > 0.5) {
          primaryMacro = 'carbohydrates';
          macroAdjustment = 'carbs';
        } else if (fatPct > 0.5) {
          primaryMacro = 'healthy fats';
          macroAdjustment = 'fat';
        }
        
        foodToAdd.actualServingAmount = amount;
        
        recommendation = {
          type: 'add',
          title: `Add ${foodToAdd.name} for ${primaryMacro.charAt(0).toUpperCase() + primaryMacro.slice(1)}`,
          description: `Add ${amount} ${foodToAdd.selectedServingUnit} of ${foodToAdd.name} to add ${caloriesAdded} calories, ${proteinAdded}g protein, ${carbsAdded}g carbs, and ${fatAdded}g fat to better balance your meal.`,
          action: 'add',
          actionLabel: 'Add Food',
          macroAdjustment,
          foodToAdd,
          macroChanges: {
            calories: caloriesAdded,
            protein: proteinAdded,
            carbs: carbsAdded,
            fat: fatAdded
          }
        };
      } else if (bestAdjustment.type === 'remove') {
        const food = bestAdjustment.food;
        
        // Calculate the macro changes
        const caloriesRemoved = Math.round(food.actualCalories);
        const proteinRemoved = Math.round(food.actualProtein * 10) / 10;
        const carbsRemoved = Math.round(food.actualCarbs * 10) / 10;
        const fatRemoved = Math.round(food.actualFat * 10) / 10;
        
        // Determine the primary macro affected
        let primaryMacro = 'meal balance';
        let macroAdjustment = 'overall';
        
        // Check which macro is most significantly affected
        const proteinPct = proteinRemoved / targetProtein;
        const carbsPct = carbsRemoved / targetCarbs;
        const fatPct = fatRemoved / targetFat;
        const calPct = caloriesRemoved / targetCals;
        
        if (calPct > proteinPct && calPct > carbsPct && calPct > fatPct) {
          primaryMacro = 'calorie balance';
          macroAdjustment = 'calories';
        } else if (proteinPct > carbsPct && proteinPct > fatPct) {
          primaryMacro = 'protein balance';
          macroAdjustment = 'protein';
        } else if (carbsPct > fatPct) {
          primaryMacro = 'carbohydrate balance';
          macroAdjustment = 'carbs';
        } else {
          primaryMacro = 'fat balance';
          macroAdjustment = 'fat';
        }
        
        recommendation = {
          type: 'remove',
          title: `Remove ${food.name} for Better ${primaryMacro.charAt(0).toUpperCase() + primaryMacro.slice(1)}`,
          description: `Remove ${food.name} (${food.actualServingAmount} ${food.selectedServingUnit}) to reduce the meal by ${caloriesRemoved} calories, ${proteinRemoved}g protein, ${carbsRemoved}g carbs, and ${fatRemoved}g fat.`,
          action: 'remove',
          actionLabel: 'Remove Food',
          macroAdjustment,
          foodToRemove: food,
          macroChanges: {
            calories: -caloriesRemoved,
            protein: -proteinRemoved,
            carbs: -carbsRemoved,
            fat: -fatRemoved
          }
        };
      }
    }
    
    // Set the recommendation
    if (recommendation) {
      setCurrentRecommendation(recommendation);
      setShowSmartRecommendation(true);
    } else {
      setCurrentRecommendation(null);
      setShowSmartRecommendation(false);
    }
  };

  // Apply the current recommendation
  const applyRecommendation = () => {
    if (!currentRecommendation) return;
    
    if (currentRecommendation.type === 'adjust') {
      // Adjust an existing food
      const updatedFoods = editedFoods.map(food => {
        if (food.id === currentRecommendation.foodToAdjust.id) {
          // Get conversion factor for selected serving unit
          const conversionFactor = getConversionFactor(food, food.selectedServingUnit);
          
          // Convert to base serving size for nutrition calculation
          const baseServingMultiplier = (currentRecommendation.newAmount * conversionFactor) / food.servingSize;
          
          return {
            ...food,
            actualServingAmount: currentRecommendation.newAmount,
            actualCalories: food.calories * baseServingMultiplier,
            actualProtein: food.protein * baseServingMultiplier,
            actualCarbs: food.carbs * baseServingMultiplier,
            actualFat: food.fat * baseServingMultiplier
          };
        }
        return food;
      });
      
      setEditedFoods(updatedFoods);
      
      // Recalculate totals
      const newTotals = updatedFoods.reduce((acc, item) => ({
        calories: acc.calories + item.actualCalories,
        protein: acc.protein + item.actualProtein,
        carbs: acc.carbs + item.actualCarbs,
        fat: acc.fat + item.actualFat
      }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
      
      setEditedTotals(newTotals);
      
      // Update meal plan
      onMealPlanUpdate({
        foods: updatedFoods,
        totals: newTotals,
        score: mealPlan.score
      });
    } else if (currentRecommendation.type === 'add' && onAddFood) {
      // Add a new food
      onAddFood(currentRecommendation.foodToAdd);
    } else if (currentRecommendation.type === 'remove' && onRemoveFood) {
      // Remove a food
      onRemoveFood(currentRecommendation.foodToRemove.id);
    }
    
    // Mark recommendation as applied and hide it
    setRecommendationApplied(true);
    setShowSmartRecommendation(false);
    
    // After a brief delay, hide the applied message
    setTimeout(() => {
      setRecommendationApplied(false);
      
      // Check if further optimization is needed
      setTimeout(() => {
        const newTotals = mealPlan.foods.reduce((acc, item) => ({
          calories: acc.calories + item.actualCalories,
          protein: acc.protein + item.actualProtein,
          carbs: acc.carbs + item.actualCarbs,
          fat: acc.fat + item.actualFat
        }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
        
        if (!isWithinAllThresholds(newTotals, targets, thresholds)) {
          generateHolisticRecommendation(mealPlan.foods, targets, thresholds, selectedFoods);
        } else {
          setCurrentRecommendation(null);
          setShowSmartRecommendation(false);
        }
      }, 500);
    }, 3000);
  };

  // Generate recommendations for failed optimizations
  const generateRecommendations = () => {
    const recommendations = [];
    
    if (isOptimizationFailed) {
      recommendations.push({
        type: 'error',
        title: 'No Valid Combination Found',
        message: 'The optimizer couldn\'t find a meal combination that meets your criteria.',
        suggestions: [
          'Increase your macro thresholds (try 15-20% instead of 10%)',
          'Expand the serving ranges for your selected foods',
          'Add more food variety to give the optimizer more options',
          'Consider switching to simple mode (calories only) first'
        ]
      });
      return recommendations;
    }

    // Using edited totals in edit mode, otherwise original totals
    const displayTotals = editMode ? editedTotals : totals;
    const calorieStatus = getAccuracyStatus(displayTotals.calories, targetCals, thresholds.calories);
    const proteinStatus = getAccuracyStatus(displayTotals.protein, targetProtein, thresholds.protein);
    const carbsStatus = getAccuracyStatus(displayTotals.carbs, targetCarbs, thresholds.carbs);
    const fatStatus = getAccuracyStatus(displayTotals.fat, targetFat, thresholds.fat);

    // Check for threshold violations
    if (calorieStatus.status === 'warning') {
      recommendations.push({
        type: 'warning',
        title: 'Calorie Target Missed',
        message: `Calories are ${Math.abs(calorieStatus.percentage)}% ${calorieStatus.percentage > 0 ? 'above' : 'below'} target.`,
        suggestions: [
          calorieStatus.percentage > 0 
            ? 'Reduce serving sizes or add lower-calorie foods'
            : 'Increase serving sizes or add higher-calorie foods',
          'Increase calorie threshold to be more flexible',
          'Adjust the serving ranges for high-calorie foods'
        ]
      });
    }

    if (thresholds.advancedMode) {
      if (proteinStatus.status === 'warning' && targetProtein > 0) {
        recommendations.push({
          type: 'warning',
          title: 'Protein Target Missed',
          message: `Protein is ${Math.abs(proteinStatus.percentage)}% ${proteinStatus.percentage > 0 ? 'above' : 'below'} target.`,
          suggestions: [
            proteinStatus.percentage > 0 
              ? 'Reduce protein-rich foods or add more carbs/fats'
              : 'Add more protein-rich foods like chicken, fish, or protein powder',
            'Increase protein threshold for more flexibility',
            'Adjust serving ranges for protein sources'
          ]
        });
      }

      if (carbsStatus.status === 'warning' && targetCarbs > 0) {
        recommendations.push({
          type: 'warning',
          title: 'Carbohydrate Target Missed',
          message: `Carbs are ${Math.abs(carbsStatus.percentage)}% ${carbsStatus.percentage > 0 ? 'above' : 'below'} target.`,
          suggestions: [
            carbsStatus.percentage > 0 
              ? 'Reduce carb-rich foods or add more protein/fats'
              : 'Add more carb-rich foods like rice, oats, or fruits',
            'Increase carb threshold for more flexibility',
            'Adjust serving ranges for carb sources'
          ]
        });
      }

      if (fatStatus.status === 'warning' && targetFat > 0) {
        recommendations.push({
          type: 'warning',
          title: 'Fat Target Missed',
          message: `Fat is ${Math.abs(fatStatus.percentage)}% ${fatStatus.percentage > 0 ? 'above' : 'below'} target.`,
          suggestions: [
            fatStatus.percentage > 0 
              ? 'Reduce fat-rich foods or add more protein/carbs'
              : 'Add more fat-rich foods like oils, nuts, or avocado',
            'Increase fat threshold for more flexibility',
            'Adjust serving ranges for fat sources'
          ]
        });
      }
    }

    // General optimization tips
    if (recommendations.length === 0) {
      recommendations.push({
        type: 'success',
        title: 'Great Optimization!',
        message: 'Your meal meets all the specified targets within thresholds.',
        suggestions: [
          'You can try tightening thresholds for more precise results',
          'Experiment with different food combinations',
          'Consider adding variety with different food sources'
        ]
      });
    }

    return recommendations;
  };

  const recommendations = generateRecommendations();

  // Share meal plan with others
  const shareMealPlan = () => {
    setShowShareOptions(true);
  };

  // Detect if user is on mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const shareToSocialMedia = (platform) => {
    // Create a shareable text message
    const foodList = mealPlan.foods.map(food => 
      `${food.name}: ${food.actualServingAmount} ${food.selectedServingUnit}`
    ).join('\n• ');
    
    const macroText = `Calories: ${Math.round(totals.calories)} | Protein: ${Math.round(totals.protein)}g | Carbs: ${Math.round(totals.carbs)}g | Fat: ${Math.round(totals.fat)}g`;
    
    const shareText = `Check out my optimized meal plan created with CreateMyMeal!\n\n• ${foodList}\n\n${macroText}\n\nCreate your own at: www.createmymeal.com`;
    
    // Handle different platforms with mobile app prioritization
    let url = '';
    switch(platform) {
      case 'pdf':
        exportToPDF();
        break;
      case 'facebook':
        if (isMobile) {
          url = `fb://share?quote=${encodeURIComponent(shareText)}`;
          // Fallback to web version
          setTimeout(() => {
            window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://www.createmymeal.com')}&quote=${encodeURIComponent(shareText)}`, '_blank');
          }, 1000);
        } else {
          url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://www.createmymeal.com')}&quote=${encodeURIComponent(shareText)}`;
        }
        break;
      case 'instagram':
        if (isMobile) {
          // Instagram doesn't support direct text sharing, but we can try to open the app
          url = `instagram://camera`;
          // Copy text to clipboard for user to paste
          navigator.clipboard.writeText(shareText);
          alert('Text copied to clipboard! Paste it in your Instagram story or post.');
        } else {
          // On desktop, just copy to clipboard
          navigator.clipboard.writeText(shareText);
          alert('Text copied to clipboard! Open Instagram on your phone and paste.');
        }
        break;
      case 'whatsapp':
        if (isMobile) {
          url = `whatsapp://send?text=${encodeURIComponent(shareText)}`;
          // Fallback to web version
          setTimeout(() => {
            window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
          }, 1000);
        } else {
          url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
        }
        break;
      case 'email':
        url = `mailto:?subject=${encodeURIComponent('My CreateMyMeal Plan')}&body=${encodeURIComponent(shareText)}`;
        break;
      default:
        // Copy to clipboard
        navigator.clipboard.writeText(shareText).then(() => {
          alert('Meal plan copied to clipboard!');
        });
        break;
    }
    
    if (url && !['instagram', 'pdf'].includes(platform)) {
      if (isMobile && ['facebook', 'whatsapp'].includes(platform)) {
        // Try to open mobile app first
        window.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    }
    
    setShowShareOptions(false);
  };

  // Export the meal plan to PDF
  const exportToPDF = async () => {
    setExportingPDF(true);
    
    try {
      // Create a temporary container for the PDF content
      const pdfContainer = document.createElement('div');
      pdfContainer.id = 'meal-plan-pdf';
      pdfContainer.style.width = '800px';
      pdfContainer.style.padding = '40px';
      pdfContainer.style.backgroundColor = 'white';
      pdfContainer.style.position = 'absolute';
      pdfContainer.style.left = '-9999px';
      pdfContainer.style.minHeight = '1000px'; // Ensure minimum height
      document.body.appendChild(pdfContainer);

      // Create the PDF content with improved layout
      pdfContainer.innerHTML = `
        <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; display: flex; flex-direction: column; min-height: 100vh;">
          <div style="text-align: center; margin-bottom: 30px;">
            <h1 style="color: #4f46e5; font-size: 32px; margin-bottom: 10px;">Your Optimized Meal Plan</h1>
            <p style="color: #6b7280; font-size: 18px;">Perfectly balanced meal to meet your macro goals</p>
          </div>

          <div style="display: flex; justify-content: space-between; margin-bottom: 40px; flex-wrap: wrap;">
            <div style="border: 2px solid #e0e7ff; background-color: #f5f5ff; border-radius: 8px; padding: 20px; width: 22%; box-sizing: border-box; text-align: center;">
              <div style="font-weight: bold; font-size: 28px; color: #4f46e5;">${Math.round(totals.calories)}</div>
              <div style="color: #6b7280; font-size: 16px;">Calories</div>
            </div>
            <div style="border: 2px solid #fee2e2; background-color: #fff5f5; border-radius: 8px; padding: 20px; width: 22%; box-sizing: border-box; text-align: center;">
              <div style="font-weight: bold; font-size: 28px; color: #dc2626;">${Math.round(totals.protein)}g</div>
              <div style="color: #6b7280; font-size: 16px;">Protein</div>
            </div>
            <div style="border: 2px solid #fef3c7; background-color: #fffbeb; border-radius: 8px; padding: 20px; width: 22%; box-sizing: border-box; text-align: center;">
              <div style="font-weight: bold; font-size: 28px; color: #d97706;">${Math.round(totals.carbs)}g</div>
              <div style="color: #6b7280; font-size: 16px;">Carbs</div>
            </div>
            <div style="border: 2px solid #d1fae5; background-color: #ecfdf5; border-radius: 8px; padding: 20px; width: 22%; box-sizing: border-box; text-align: center;">
              <div style="font-weight: bold; font-size: 28px; color: #059669;">${Math.round(totals.fat)}g</div>
              <div style="color: #6b7280; font-size: 16px;">Fat</div>
            </div>
          </div>

          <div style="flex: 1;">
            <h2 style="color: #4f46e5; font-size: 24px; margin-bottom: 20px; border-bottom: 2px solid #e0e7ff; padding-bottom: 10px;">Food Breakdown</h2>
            <div style="margin-bottom: 40px;">
              ${mealPlan.foods.map(food => `
                <div style="background-color: #f9fafb; border-radius: 8px; padding: 20px; margin-bottom: 15px; page-break-inside: avoid;">
                  <div style="display: flex; justify-content: space-between; margin-bottom: 15px;">
                    <div>
                      <div style="font-weight: bold; font-size: 20px; color: #1f2937;">${food.name}</div>
                      <div style="font-size: 16px; color: #6b7280;">${food.category}</div>
                    </div>
                    <div style="text-align: right;">
                      <div style="font-weight: bold; font-size: 24px; color: #4f46e5;">${food.actualServingAmount} ${food.selectedServingUnit}</div>
                    </div>
                  </div>
                  <div style="display: flex; justify-content: space-between; font-size: 16px;">
                    <div style="text-align: center; width: 23%;">
                      <div style="font-weight: bold; color: #f97316;">${Math.round(food.actualCalories)}</div>
                      <div style="color: #6b7280;">calories</div>
                    </div>
                    <div style="text-align: center; width: 23%;">
                      <div style="font-weight: bold; color: #dc2626;">${Math.round(food.actualProtein * 10) / 10}g</div>
                      <div style="color: #6b7280;">protein</div>
                    </div>
                    <div style="text-align: center; width: 23%;">
                      <div style="font-weight: bold; color: #d97706;">${Math.round(food.actualCarbs * 10) / 10}g</div>
                      <div style="color: #6b7280;">carbs</div>
                    </div>
                    <div style="text-align: center; width: 23%;">
                      <div style="font-weight: bold; color: #059669;">${Math.round(food.actualFat * 10) / 10}g</div>
                      <div style="color: #6b7280;">fat</div>
                    </div>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>

          <div style="margin-top: auto; text-align: center; padding-top: 30px; padding-bottom: 50px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 16px;">
            Created with <span style="color: #4f46e5; font-weight: bold;">CreateMyMeal</span> • www.createmymeal.com
          </div>
        </div>
      `;

      // Wait for content to render
      await new Promise(resolve => setTimeout(resolve, 100));

      // Generate PDF from the content
      const canvas = await html2canvas(pdfContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        height: pdfContainer.scrollHeight,
        windowHeight: pdfContainer.scrollHeight
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Create PDF with proper sizing
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Calculate scaling to fit width
      const ratio = pdfWidth / imgWidth;
      const scaledHeight = imgHeight * ratio;
      
      // If content is taller than one page, we need to handle multiple pages
      if (scaledHeight > pdfHeight) {
        let yPosition = 0;
        const pageHeight = pdfHeight - 20; // Leave some extra margin at bottom
        
        while (yPosition < scaledHeight) {
          // Add new page if not the first page
          if (yPosition > 0) {
            pdf.addPage();
          }
          
          // Calculate the portion of the image to include on this page
          const sourceY = yPosition / ratio;
          const sourceHeight = Math.min(pageHeight / ratio, imgHeight - sourceY);
          
          // Create a canvas for this page portion
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = imgWidth;
          pageCanvas.height = sourceHeight;
          const pageCtx = pageCanvas.getContext('2d');
          
          // Draw the portion of the original image
          pageCtx.drawImage(canvas, 0, sourceY, imgWidth, sourceHeight, 0, 0, imgWidth, sourceHeight);
          
          const pageImgData = pageCanvas.toDataURL('image/png');
          pdf.addImage(pageImgData, 'PNG', 0, 0, pdfWidth, sourceHeight * ratio);
          
          yPosition += pageHeight;
        }
        
        // Add footer to the last page
        const footerText = 'Created with CreateMyMeal • www.createmymeal.com';
        pdf.setFontSize(10);
        pdf.setTextColor(107, 114, 128); // Gray color
        pdf.text(footerText, pdfWidth / 2, pdfHeight - 10, { align: 'center' });
        
      } else {
        // Single page
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, scaledHeight);
        
        // Add footer with extra space
        const footerText = 'Created with CreateMyMeal • www.createmymeal.com';
        pdf.setFontSize(10);
        pdf.setTextColor(107, 114, 128); // Gray color
        pdf.text(footerText, pdfWidth / 2, pdfHeight - 10, { align: 'center' });
      }
      
      pdf.save('CreateMyMeal_Plan.pdf');
      
      // Clean up
      document.body.removeChild(pdfContainer);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setExportingPDF(false);
    }
  };

  // If optimization failed, show failure state with recommendations
  if (isOptimizationFailed) {
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="bg-white rounded-2xl shadow-lg overflow-hidden"
      >
        <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white p-6">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiAlertTriangle} className="text-3xl" />
            <div>
              <h2 className="text-2xl font-bold">Optimization Failed</h2>
              <p className="text-red-100 mt-1">
                Couldn't find a valid meal combination with your current settings
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Selected Foods Display */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <SafeIcon icon={FiTarget} className="text-indigo-600" />
              Your Selected Foods
            </h3>
            <div className="space-y-3">
              {selectedFoods.map((food) => (
                <div key={food.id} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-medium text-gray-800">{food.name}</h4>
                      <span className="text-sm text-gray-600">{food.category}</span>
                    </div>
                    <div className="text-right text-sm text-gray-600">
                      Range: {food.desiredMin}-{food.desiredMax} {food.selectedServingUnit}
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div className="text-center">
                      <div className="font-semibold text-orange-600">{food.calories}</div>
                      <div className="text-gray-600">kcal</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-red-600">{food.protein}g</div>
                      <div className="text-gray-600">protein</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-yellow-600">{food.carbs}g</div>
                      <div className="text-gray-600">carbs</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-green-600">{food.fat}g</div>
                      <div className="text-gray-600">fat</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-orange-50 p-6 rounded-xl">
            <h3 className="text-lg font-semibold text-orange-800 mb-4 flex items-center gap-2">
              <SafeIcon icon={FiInfo} className="text-xl" />
              Recommendations to Fix This
            </h3>
            {recommendations.map((rec, index) => (
              <div key={index} className="mb-4 last:mb-0">
                <h4 className="font-medium text-orange-900 mb-2">{rec.title}</h4>
                <p className="text-orange-800 text-sm mb-3">{rec.message}</p>
                <ul className="space-y-1">
                  {rec.suggestions.map((suggestion, i) => (
                    <li key={i} className="text-sm text-orange-700 flex items-start gap-2">
                      <span className="text-orange-500 mt-1">•</span>
                      <span>{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="bg-indigo-50 p-4 rounded-lg">
            <h4 className="font-medium text-indigo-800 mb-3">Quick Actions:</h4>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
              >
                Try Again
              </button>
              <button
                onClick={() => {
                  // This would need to be connected to the parent component
                  console.log('Increase thresholds');
                }}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm"
              >
                Increase Thresholds
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Using edited totals in edit mode, otherwise original totals
  const displayTotals = editMode ? editedTotals : totals;
  const calorieStatus = getAccuracyStatus(displayTotals.calories, targetCals, thresholds.calories);
  const proteinStatus = getAccuracyStatus(displayTotals.protein, targetProtein, thresholds.protein);
  const carbsStatus = getAccuracyStatus(displayTotals.carbs, targetCarbs, thresholds.carbs);
  const fatStatus = getAccuracyStatus(displayTotals.fat, targetFat, thresholds.fat);

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-green-600 bg-green-50 border-green-200';
      case 'warning': return 'text-orange-600 bg-orange-50 border-orange-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    return status === 'success' ? FiCheckCircle : FiAlertCircle;
  };

  const getThresholdText = (macro) => {
    const threshold = thresholds[macro];
    return threshold > 0 ? `±${threshold}%` : 'No limit';
  };

  const roundToHalf = (value, servingUnit) => {
    // For countable items like eggs, round to nearest 0.5
    if (['egg', 'slice', 'piece', 'medium', 'large', 'small', 'fillet', 'patty', 'steak', 'thigh', 'head', 'handful', 'half', 'ball'].includes(servingUnit)) {
      return Math.round(value * 2) / 2; // Round to nearest 0.5
    }
    
    // For weight-based measurements, round to nearest whole number
    if (['g', 'oz', 'kg'].includes(servingUnit)) {
      return Math.round(value);
    }
    
    // For volume measurements, round to nearest 0.5
    return Math.round(value * 2) / 2;
  };

  const handleServingChange = (foodId, newAmount) => {
    const updatedFoods = editedFoods.map(food => {
      if (food.id === foodId) {
        // Get conversion factor for selected serving unit
        const conversionFactor = getConversionFactor(food, food.selectedServingUnit);
        
        // Round to appropriate precision based on serving unit
        const roundedAmount = roundToHalf(newAmount, food.selectedServingUnit);
        
        // Convert to base serving size for nutrition calculation
        const baseServingMultiplier = (roundedAmount * conversionFactor) / food.servingSize;

        return {
          ...food,
          actualServingAmount: roundedAmount,
          actualCalories: food.calories * baseServingMultiplier,
          actualProtein: food.protein * baseServingMultiplier,
          actualCarbs: food.carbs * baseServingMultiplier,
          actualFat: food.fat * baseServingMultiplier
        };
      }
      return food;
    });

    setEditedFoods(updatedFoods);

    // Recalculate totals
    const newTotals = updatedFoods.reduce((acc, item) => ({
      calories: acc.calories + item.actualCalories,
      protein: acc.protein + item.actualProtein,
      carbs: acc.carbs + item.actualCarbs,
      fat: acc.fat + item.actualFat
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

    setEditedTotals(newTotals);
  };

  const saveChanges = () => {
    if (editMode) {
      onMealPlanUpdate({
        foods: editedFoods,
        totals: editedTotals,
        score: mealPlan.score
      });
      setEditMode(false);
      
      // Check if we need to show recommendations after manual edits
      setTimeout(() => {
        if (!isWithinAllThresholds(editedTotals, targets, thresholds)) {
          generateHolisticRecommendation(editedFoods, targets, thresholds, selectedFoods);
        }
      }, 500);
    } else {
      setEditMode(true);
    }
  };

  const handleRemoveFood = (foodId) => {
    // Only allow removing in edit mode
    if (!editMode) return;
    
    const updatedFoods = editedFoods.filter(food => food.id !== foodId);
    
    // Recalculate totals
    const newTotals = updatedFoods.reduce((acc, item) => ({
      calories: acc.calories + item.actualCalories,
      protein: acc.protein + item.actualProtein,
      carbs: acc.carbs + item.actualCarbs,
      fat: acc.fat + item.actualFat
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
    
    setEditedFoods(updatedFoods);
    setEditedTotals(newTotals);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="bg-white rounded-2xl shadow-lg overflow-hidden"
    >
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiPieChart} className="text-2xl" />
            <h2 className="text-2xl font-bold">Your Optimized Meal</h2>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowRecommendations(!showRecommendations)}
              className="flex items-center gap-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiInfo} className="text-lg" />
              <span>Tips</span>
            </button>
            <button
              onClick={shareMealPlan}
              className="flex items-center gap-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiShare2} className="text-lg" />
              <span>Share</span>
            </button>
            <button
              onClick={saveChanges}
              className="flex items-center gap-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <SafeIcon icon={editMode ? FiSave : FiEdit2} className="text-lg" />
              <span>{editMode ? 'Save Changes' : 'Edit Servings'}</span>
            </button>
          </div>
        </div>
        <p className="text-indigo-100 mt-2">
          {editMode ? 'Adjust serving sizes to meet your preferences' : 'Serving sizes optimized within your desired ranges and thresholds'}
        </p>
      </div>

      <div className="p-6 space-y-6">
        {/* Share Options Modal */}
        <AnimatePresence>
          {showShareOptions && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
              onClick={() => setShowShareOptions(false)}
            >
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 50, opacity: 0 }}
                className="bg-white rounded-xl p-6 max-w-md w-full"
                onClick={e => e.stopPropagation()}
              >
                <h3 className="text-xl font-bold text-gray-800 mb-4">Share Your Meal Plan</h3>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => shareToSocialMedia('pdf')}
                    className="flex items-center justify-center gap-2 bg-indigo-600 text-white py-3 px-4 rounded-lg hover:bg-indigo-700 transition-colors col-span-2"
                    disabled={exportingPDF}
                  >
                    {exportingPDF ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                        <span>Generating PDF...</span>
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiFileText} className="text-lg" />
                        <span>Export as PDF</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => shareToSocialMedia('facebook')}
                    className="flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <span className="text-lg">f</span>
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => shareToSocialMedia('instagram')}
                    className="flex items-center justify-center gap-2 bg-pink-600 text-white py-3 px-4 rounded-lg hover:bg-pink-700 transition-colors"
                  >
                    <SafeIcon icon={FiInstagram} className="text-lg" />
                    <span>Instagram</span>
                  </button>
                  <button
                    onClick={() => shareToSocialMedia('whatsapp')}
                    className="flex items-center justify-center gap-2 bg-green-500 text-white py-3 px-4 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <span className="text-lg">W</span>
                    <span>WhatsApp</span>
                  </button>
                  <button
                    onClick={() => shareToSocialMedia('email')}
                    className="flex items-center justify-center gap-2 bg-gray-600 text-white py-3 px-4 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <SafeIcon icon={FiMail} className="text-lg" />
                    <span>Email</span>
                  </button>
                  <button
                    onClick={() => shareToSocialMedia('clipboard')}
                    className="flex items-center justify-center gap-2 bg-indigo-600 text-white py-3 px-4 rounded-lg hover:bg-indigo-700 transition-colors col-span-2"
                  >
                    <span>Copy to Clipboard</span>
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Smart Recommendation */}
        <AnimatePresence>
          {showSmartRecommendation && currentRecommendation && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border-2 border-indigo-100"
            >
              <div className="flex items-start gap-4">
                <div className="bg-indigo-100 rounded-full p-3 mt-1">
                  <SafeIcon icon={currentRecommendation.action === 'increase' ? FiPlus : currentRecommendation.action === 'decrease' ? FiMinus : currentRecommendation.action === 'remove' ? FiTrash2 : FiChevronRight} className="text-xl text-indigo-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-indigo-800 mb-2">
                    {currentRecommendation.title}
                  </h3>
                  <p className="text-indigo-700 mb-3">
                    {currentRecommendation.description}
                  </p>
                  {/* Macro Changes Display */}
                  {currentRecommendation.macroChanges && (
                    <div className="bg-white bg-opacity-50 rounded-lg p-3 mb-4">
                      <div className="text-sm font-medium text-indigo-800 mb-2">Estimated Changes:</div>
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <span className="text-orange-600 font-medium">
                            {currentRecommendation.macroChanges.calories > 0 ? '+' : ''}
                            {Math.round(currentRecommendation.macroChanges.calories)}
                          </span>
                          <span className="text-xs text-gray-600 ml-1">kcal</span>
                        </div>
                        <div>
                          <span className="text-red-600 font-medium">
                            {currentRecommendation.macroChanges.protein > 0 ? '+' : ''}
                            {Math.round(currentRecommendation.macroChanges.protein * 10) / 10}
                          </span>
                          <span className="text-xs text-gray-600 ml-1">g protein</span>
                        </div>
                        <div>
                          <span className="text-yellow-600 font-medium">
                            {currentRecommendation.macroChanges.carbs > 0 ? '+' : ''}
                            {Math.round(currentRecommendation.macroChanges.carbs * 10) / 10}
                          </span>
                          <span className="text-xs text-gray-600 ml-1">g carbs</span>
                        </div>
                        <div>
                          <span className="text-green-600 font-medium">
                            {currentRecommendation.macroChanges.fat > 0 ? '+' : ''}
                            {Math.round(currentRecommendation.macroChanges.fat * 10) / 10}
                          </span>
                          <span className="text-xs text-gray-600 ml-1">g fat</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-indigo-600">
                      This will help balance your overall meal macros.
                    </div>
                    <button
                      onClick={applyRecommendation}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors text-sm flex items-center gap-2"
                    >
                      <SafeIcon icon={FiChevronRight} className="text-sm" />
                      <span>{currentRecommendation.actionLabel}</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Recommendation Applied Message */}
        <AnimatePresence>
          {recommendationApplied && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-center gap-3"
            >
              <SafeIcon icon={FiCheckCircle} className="text-xl text-green-600" />
              <div>
                <h4 className="font-medium text-green-800">Recommendation Applied</h4>
                <p className="text-sm text-green-700">
                  The changes have been applied to your meal plan.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Recommendations */}
        {showRecommendations && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-blue-50 p-6 rounded-xl"
          >
            <h3 className="text-lg font-semibold text-blue-800 mb-4 flex items-center gap-2">
              <SafeIcon icon={FiInfo} className="text-xl" />
              Optimization Tips & Recommendations
            </h3>
            {recommendations.map((rec, index) => (
              <div key={index} className="mb-4 last:mb-0">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <SafeIcon icon={rec.type === 'success' ? FiCheckCircle : rec.type === 'warning' ? FiAlertCircle : FiAlertTriangle} className={`text-lg ${rec.type === 'success' ? 'text-green-600' : rec.type === 'warning' ? 'text-orange-600' : 'text-red-600'}`} />
                  {rec.title}
                </h4>
                <p className="text-blue-800 text-sm mb-3">{rec.message}</p>
                <ul className="space-y-1">
                  {rec.suggestions.map((suggestion, i) => (
                    <li key={i} className="text-sm text-blue-700 flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </motion.div>
        )}

        {/* Macro Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {label: 'Calories', actual: displayTotals.calories, target: targetCals, unit: 'kcal', status: calorieStatus, threshold: thresholds.calories},
            {label: 'Protein', actual: displayTotals.protein, target: targetProtein, unit: 'g', status: proteinStatus, threshold: thresholds.protein},
            {label: 'Carbs', actual: displayTotals.carbs, target: targetCarbs, unit: 'g', status: carbsStatus, threshold: thresholds.carbs},
            {label: 'Fat', actual: displayTotals.fat, target: targetFat, unit: 'g', status: fatStatus, threshold: thresholds.fat}
          ].map((macro) => (
            <div key={macro.label} className={`p-4 rounded-lg border-2 ${getStatusColor(macro.status.status)}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{macro.label}</span>
                <SafeIcon icon={getStatusIcon(macro.status.status)} className="text-lg" />
              </div>
              <div className="text-xl font-bold mb-1 flex items-baseline gap-1">
                <span>{Math.round(macro.actual * 10) / 10}</span>
                <span className="text-sm font-normal">{macro.unit}</span>
              </div>
              {macro.target > 0 && (
                <div className="text-sm space-y-1">
                  <div>Target: {macro.target}{macro.unit}</div>
                  <div className="text-xs text-gray-500">
                    Threshold: {getThresholdText(macro.label.toLowerCase())}
                  </div>
                  <div className={`font-medium ${macro.status.status === 'success' ? 'text-green-600' : 'text-orange-600'}`}>
                    {macro.status.percentage > 0 ? '+' : ''}{macro.status.percentage}%
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Food Breakdown */}
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <SafeIcon icon={FiTrendingUp} className="text-indigo-600" />
            {editMode ? 'Edit Serving Sizes' : 'Optimized Serving Sizes'}
          </h3>
          <div className="space-y-3">
            {(editMode ? editedFoods : mealPlan.foods).map((food) => (
              <motion.div
                key={food.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-50 rounded-lg p-4"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-800">{food.name}</h4>
                    <span className="text-sm text-gray-600">{food.category}</span>
                    {food.isCustom && (
                      <span className="ml-2 bg-indigo-200 text-indigo-800 px-2 py-0.5 rounded-full text-xs">
                        Custom
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    {editMode ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleServingChange(food.id, Math.max(0, food.actualServingAmount - 0.5))}
                          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded"
                        >
                          <SafeIcon icon={FiMinus} className="text-sm" />
                        </button>
                        <input
                          type="number"
                          value={food.actualServingAmount}
                          onChange={(e) => handleServingChange(food.id, parseFloat(e.target.value) || 0)}
                          min="0"
                          step={['egg', 'slice', 'piece', 'medium', 'large', 'small'].includes(food.selectedServingUnit) ? '0.5' : '1'}
                          className="w-24 px-2 py-1 text-lg font-bold text-indigo-600 border-2 border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-center"
                        />
                        <button
                          onClick={() => handleServingChange(food.id, food.actualServingAmount + 0.5)}
                          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded"
                        >
                          <SafeIcon icon={FiPlus} className="text-sm" />
                        </button>
                        <span className="text-gray-600 ml-2">{food.selectedServingUnit}</span>
                        {/* Remove button (only in edit mode) */}
                        <button
                          onClick={() => handleRemoveFood(food.id)}
                          className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full"
                          title="Remove food"
                        >
                          <SafeIcon icon={FiTrash2} className="text-sm" />
                        </button>
                      </div>
                    ) : (
                      <div className="text-2xl font-bold text-indigo-600">
                        {food.actualServingAmount} {food.selectedServingUnit}
                      </div>
                    )}
                    <div className="text-sm text-gray-600">
                      Range: {food.desiredMin}-{food.desiredMax} {food.selectedServingUnit}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div className="text-center">
                    <div className="font-semibold text-orange-600">
                      {Math.round(food.actualCalories)}
                    </div>
                    <div className="text-gray-600">kcal</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-red-600">
                      {Math.round(food.actualProtein * 10) / 10}g
                    </div>
                    <div className="text-gray-600">protein</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-yellow-600">
                      {Math.round(food.actualCarbs * 10) / 10}g
                    </div>
                    <div className="text-gray-600">carbs</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-green-600">
                      {Math.round(food.actualFat * 10) / 10}g
                    </div>
                    <div className="text-gray-600">fat</div>
                  </div>
                </div>
                {/* Range indicator - only show in non-edit mode */}
                {!editMode && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                    <SafeIcon icon={FiTarget} className="text-indigo-500" />
                    <span>
                      {food.actualServingAmount >= food.desiredMin && food.actualServingAmount <= food.desiredMax
                        ? `Within desired range (${food.desiredMin}-${food.desiredMax} ${food.selectedServingUnit})`
                        : `Outside desired range (${food.desiredMin}-${food.desiredMax} ${food.selectedServingUnit})`}
                    </span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Accuracy Summary */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-2">Optimization Summary</h4>
          <div className="text-sm text-gray-600 space-y-2">
            {!editMode && (
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiCheckCircle} className="text-green-600" />
                <span>All serving sizes are within your desired ranges</span>
              </div>
            )}
            {thresholds.advancedMode ? (
              <div>
                {[calorieStatus, proteinStatus, carbsStatus, fatStatus].every(s => s.status === 'success' || s.status === 'neutral') ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <SafeIcon icon={FiCheckCircle} />
                    <span>All active thresholds achieved! 🎯</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-orange-600">
                    <SafeIcon icon={FiAlertCircle} />
                    <span>Some thresholds exceeded. This is the best optimization possible with your selected foods and ranges.</span>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {calorieStatus.status === 'success' ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <SafeIcon icon={FiCheckCircle} />
                    <span>Calorie target achieved within {thresholds.calories}% threshold! 🎯</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-orange-600">
                    <SafeIcon icon={FiAlertCircle} />
                    <span>Calorie target outside {thresholds.calories}% threshold. This is the best optimization possible with your selected foods and ranges.</span>
                  </div>
                )}
              </div>
            )}
            {editMode && (
              <div className="flex items-center gap-2 text-indigo-600 mt-2 pt-2 border-t border-gray-200">
                <SafeIcon icon={FiEdit2} />
                <span>Edit mode: Adjust serving sizes and save changes when done. You can set servings outside the original range.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MealResults;